import { ApiError, get, post } from './api';
import { parseLegalAcceptanceRequired } from './legalAcceptanceRequired';
import type {
  LegalAcceptanceHistoryEntry,
  LegalAcceptanceRequest,
  LegalAcceptanceRequired,
} from '../types/legal';

/** The 409 code the API returns when a document changed between showing it and accepting it. */
const VERSION_CHANGED_CODE = 'version_changed';
/** The 403 code the API returns when a system admin viewing another organization tries to accept. */
const IMPERSONATION_NOT_ALLOWED_CODE = 'impersonation_not_allowed';
/** The 403 code the API returns for any write from a travel ball program that is paused. */
const TRAVEL_BALL_PROGRAM_PAUSED_CODE = 'travel_ball_organization_paused';

/** Where each kind of identity accepts. A travel ball view accepts for its travel ball identity. */
const ACCEPT_PATHS = {
  college: '/api/auth/legal-acceptance',
  travelball: '/api/travel-ball/legal/accept',
} as const;

/** Which identity the acceptance is for: the college user, or the travel ball user of a travel ball view. */
export type LegalAcceptanceAudience = keyof typeof ACCEPT_PATHS;

const IMPERSONATION_NOT_ALLOWED_FALLBACK =
  'You are viewing another organization. Switch back to your own to accept these terms.';

/**
 * The API refused the acceptance because the caller is a system admin viewing another
 * organization. `message` is the server's copy (or a fallback), meant to be shown as is.
 */
export class LegalAcceptanceNotAllowedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LegalAcceptanceNotAllowedError';
  }
}

/**
 * The API refused a travel ball acceptance because the program is paused; the pause wins over the
 * legal prompt. `message` is the server's pause copy, or null when it sent none.
 */
export class LegalAcceptanceProgramPausedError extends Error {
  readonly pauseMessage: string | null;

  constructor(pauseMessage: string | null) {
    super(pauseMessage ?? 'This travel ball program is paused.');
    this.name = 'LegalAcceptanceProgramPausedError';
    this.pauseMessage = pauseMessage;
  }
}

const responseBody = (error: ApiError): Record<string, unknown> =>
  (typeof error.response === 'object' && error.response !== null
    ? error.response
    : {}) as Record<string, unknown>;

const nonBlankMessage = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

export type LegalAcceptResult =
  | { status: 'accepted' }
  | {
      status: 'version_changed';
      /** The server's copy, or null if it sent none. */
      message: string | null;
      /** The documents to show now. Null when the server sent nothing usable. */
      legalAcceptanceRequired: LegalAcceptanceRequired | null;
    };

/**
 * Accept the versions of the Terms of Service and Privacy Policy the user was shown.
 *
 * Resolves `version_changed` (rather than throwing) when a document was updated again while the
 * dialog was open, so the caller can swap in the new one. Throws `LegalAcceptanceNotAllowedError`
 * when the API refuses a system admin viewing another organization, and
 * `LegalAcceptanceProgramPausedError` when a travel ball program is paused. Any other failure
 * throws the ApiError.
 *
 * `audience` picks the endpoint: a travel ball view accepts for its travel ball identity at
 * `POST /api/travel-ball/legal/accept`, which takes the same body and answers the same way.
 */
export const acceptLegalDocuments = async (
  shown: LegalAcceptanceRequired,
  audience: LegalAcceptanceAudience = 'college'
): Promise<LegalAcceptResult> => {
  const body: LegalAcceptanceRequest = {
    terms: shown.terms?.version ?? null,
    privacy: shown.privacy?.version ?? null,
    platform: 'web',
  };

  try {
    await post<unknown>(ACCEPT_PATHS[audience], body);
    return { status: 'accepted' };
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 409) {
      const response = responseBody(error);

      if (response.code === VERSION_CHANGED_CODE) {
        return {
          status: 'version_changed',
          message: nonBlankMessage(response.message),
          legalAcceptanceRequired: parseLegalAcceptanceRequired(response.legalAcceptanceRequired),
        };
      }
    }
    if (error instanceof ApiError && error.statusCode === 403) {
      const response = responseBody(error);

      if (response.code === IMPERSONATION_NOT_ALLOWED_CODE) {
        throw new LegalAcceptanceNotAllowedError(
          nonBlankMessage(response.message) ?? IMPERSONATION_NOT_ALLOWED_FALLBACK
        );
      }
      if (response.code === TRAVEL_BALL_PROGRAM_PAUSED_CODE) {
        throw new LegalAcceptanceProgramPausedError(nonBlankMessage(response.message));
      }
    }
    throw error;
  }
};

/** System admin: one user's acceptance history, newest first. */
export const getUserLegalAcceptances = (
  orgId: string,
  userId: string
): Promise<LegalAcceptanceHistoryEntry[]> =>
  get<LegalAcceptanceHistoryEntry[]>(
    `/api/admin/organizations/${encodeURIComponent(orgId)}/users/${encodeURIComponent(userId)}/legal-acceptances`
  );
